import { useRef, useState } from "react";
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
} from "react-native";

// Shared controller for a full-width paged horizontal ScrollView (one page
// per item): measures the real container width via onLayout (never the
// window — in the wide console this can sit in the 380px rail), tracks the
// page index off the live scroll offset (web rnw doesn't always fire
// onMomentumScrollEnd; clamp guards overscroll bounce), and pages
// programmatically so arrows/dots work where a hidden-scrollbar ScrollView
// can't be mouse-dragged. Extracted from ProgramHighlightCarousel when the
// PR badge gallery became the 2nd pager (HR-9b). The ScrollView itself stays
// bespoke in the caller — only the controller is shared.
export function usePagerScroll(count: number) {
  const [width, setWidth] = useState(0);
  const [index, setIndex] = useState(0);
  const scrollRef = useRef<ScrollView>(null);

  const goTo = (i: number) => {
    const clamped = Math.max(0, Math.min(count - 1, i));
    setIndex(clamped);
    scrollRef.current?.scrollTo({ x: clamped * width, animated: true });
  };

  const onLayout = (e: LayoutChangeEvent) => {
    const w = e.nativeEvent.layout.width;
    if (w > 0 && w !== width) setWidth(w);
  };

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (width <= 0) return;
    const i = Math.round(e.nativeEvent.contentOffset.x / width);
    const clamped = Math.max(0, Math.min(count - 1, i));
    if (clamped !== index) setIndex(clamped);
  };

  // The item list can shrink under a stale index (e.g. PRs age out of the
  // recency window on a routine refresh) — clamp on read so the caller never
  // sees "page 5 of 3"; the next scroll/goTo re-syncs the state itself.
  const safeIndex = Math.max(0, Math.min(count - 1, index));

  return { width, index: safeIndex, goTo, onLayout, onScroll, scrollRef };
}
